/**
 * Numeracja dokumentów (docs/01 D-12, docs/02 rozdz. 4).
 * Serie firmowe (oferta, umowa) mają licznik per firma i rok.
 * Serie wspólne (sprawa, zadanie) — jeden licznik dla obu firm (to nie są dokumenty księgowe).
 */

export const DOC_TYPES = {
  sprawa: { label: 'Sprawa', perCompany: false, defaultPattern: 'S/{RRRR}/{NNNN}' },
  zadanie: { label: 'Zadanie', perCompany: false, defaultPattern: 'ZD/{RRRR}/{NNNN}' },
  zgloszenie: { label: 'Zgłoszenie serwisowe', perCompany: false, defaultPattern: 'SZ/{RRRR}/{NNNN}' },
  oferta: { label: 'Oferta', perCompany: true, defaultPattern: 'OF/{FIRMA}/{RRRR}/{NNNN}' },
  umowa: { label: 'Umowa', perCompany: true, defaultPattern: 'UM/{FIRMA}/{RRRR}/{NNNN}' },
  mag_bo: { label: 'Magazyn — stan początkowy (BO)', perCompany: true, defaultPattern: 'BO/{FIRMA}/{RRRR}/{NNNN}' },
  mag_pz: { label: 'Magazyn — przyjęcie (PZ)', perCompany: true, defaultPattern: 'PZ/{FIRMA}/{RRRR}/{NNNN}' },
  mag_wz: { label: 'Magazyn — wydanie na realizację (WZ)', perCompany: true, defaultPattern: 'WZ/{FIRMA}/{RRRR}/{NNNN}' },
  mag_we: { label: 'Magazyn — wydanie na zapas ekipy (WE)', perCompany: true, defaultPattern: 'WE/{FIRMA}/{RRRR}/{NNNN}' },
  mag_zw: { label: 'Magazyn — zwrot (ZW)', perCompany: true, defaultPattern: 'ZW/{FIRMA}/{RRRR}/{NNNN}' },
  mag_mm: { label: 'Magazyn — przesunięcie (MM)', perCompany: true, defaultPattern: 'MM/{FIRMA}/{RRRR}/{NNNN}' },
  mag_inw: { label: 'Magazyn — różnice inwentaryzacji (INW)', perCompany: true, defaultPattern: 'INW/{FIRMA}/{RRRR}/{NNNN}' },
  mag_kor: { label: 'Magazyn — korekta (KOR)', perCompany: true, defaultPattern: 'KOR/{FIRMA}/{RRRR}/{NNNN}' },
  mag_pak: { label: 'Magazyn — pakiet dla ekipy', perCompany: true, defaultPattern: 'PAK/{FIRMA}/{RRRR}/{NNNN}' },
  mag_zam: { label: 'Zamówienie do dostawcy', perCompany: true, defaultPattern: 'ZAM/{FIRMA}/{RRRR}/{NNNN}' },
  /** realizacja na podstawie oferty bez umowy (szybka ścieżka poniżej progu wartości) */
  zlecenie: { label: 'Zlecenie wg oferty', perCompany: true, defaultPattern: 'ZL/{FIRMA}/{RRRR}/{NNNN}' },
} as const;

export type DocType = keyof typeof DOC_TYPES;
export const DOC_TYPE_CODES = Object.keys(DOC_TYPES) as DocType[];

const TOKEN = /\{(FIRMA|RRRR|RR|N+)\}/g;

/** Błędy wzoru numeracji (po polsku); pusta lista = wzór poprawny. */
export function validatePattern(pattern: string, perCompany: boolean): string[] {
  const errors: string[] = [];
  if (pattern.length < 3 || pattern.length > 60) errors.push('Wzór musi mieć od 3 do 60 znaków.');
  const unknown = pattern.replace(TOKEN, '').match(/[{}]/);
  if (unknown) errors.push('Wzór zawiera nieznany znacznik. Dozwolone: {FIRMA}, {RRRR}, {RR}, {NNNN}.');
  if (!/\{N+\}/.test(pattern)) errors.push('Wzór musi zawierać numer kolejny, np. {NNNN}.');
  if (!/\{RRRR\}|\{RR\}/.test(pattern)) errors.push('Wzór musi zawierać rok ({RRRR} lub {RR}) — licznik jest roczny.');
  if (perCompany && !pattern.includes('{FIRMA}'))
    errors.push('Wzór dokumentu firmowego musi zawierać {FIRMA}, żeby numery obu firm były rozróżnialne.');
  if (!perCompany && pattern.includes('{FIRMA}')) errors.push('Seria wspólna nie może zawierać {FIRMA}.');
  return errors;
}

export function formatNumber(
  pattern: string,
  v: { companyCode?: string | null; year: number; seq: number },
): string {
  return pattern.replace(TOKEN, (_m, tok: string) => {
    if (tok === 'FIRMA') return v.companyCode ?? '';
    if (tok === 'RRRR') return String(v.year);
    if (tok === 'RR') return String(v.year % 100).padStart(2, '0');
    return String(v.seq).padStart(tok.length, '0');
  });
}

export const TIME_ZONE = 'Europe/Warsaw';

/** Rok kalendarzowy w strefie Europe/Warsaw (o północy 1 stycznia czasu polskiego zaczyna się nowy licznik). */
export function yearInWarsaw(date: Date): number {
  return Number(new Intl.DateTimeFormat('en-GB', { timeZone: TIME_ZONE, year: 'numeric' }).format(date));
}

/** Dzisiejsza data (RRRR-MM-DD) w strefie Europe/Warsaw. */
export function todayInWarsaw(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/** Data przesunięta o n dni (RRRR-MM-DD, bez wpływu zmiany czasu). */
export function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
